import { buildIngredientGroups, groupedInput, kitchenResponsibility, menuSauces, SIDE_CATEGORIES, sortPlatingDishes } from './plating.js'

export const FRIDAY_SIDE_CATEGORIES = new Set(['oats', 'chia', 'chia_2'])
export const BROTH_CATEGORIES = new Set(['broth', 'broth_2'])
export const SHOT_CATEGORIES = new Set(['wellness', 'wellness_shots'])
export const STANDARD_SAUCE_OZ = 2
export const SALAD_CATEGORIES = new Set(['salad', 'salad_2'])
export const taskKeyForBase = (component) => `side-base::${component.recipe_id || component.id}`
export const isSideBase = (component) => /\b(?:pudding|oats)\b/i.test(component.recipe_name || component.name || '') && !/\b(?:crumble|granola|topping)\b/i.test(component.recipe_name || component.name || '')
// Airtable imports count the shot bundle as `wellness_shots`; menus file the dish as `wellness`.
const COUNT_KEYS = { wellness: ['wellness', 'wellness_shots'], wellness_shots: ['wellness_shots', 'wellness'] }
export const sideCount = (dish, latest) => {
  const imported = (COUNT_KEYS[dish.category] || [dish.category])
    .map((key) => latest?.meal_counts?.[key]?.total).find((total) => total != null)
  return Number(imported ?? dish.meal_count) || 0
}
const FLUID_OZ_PER_LITER = 33.814

// Broths and shots are made by the chef but plated by the prep team on
// Saturday, so Saturday sides shows what to fill without owning the checkoff.
// Status reads the same Chef work keys (`chef-broth::<recipe>`, `side-complete::<dish>`).
export function chefMadeSides(menu, latest) {
  return (menu?.dishes || []).filter((dish) => BROTH_CATEGORIES.has(dish.category) || SHOT_CATEGORIES.has(dish.category)).map((dish) => {
    const broth = BROTH_CATEGORIES.has(dish.category)
    const count = sideCount(dish, latest)
    const items = (dish.components || []).filter((component) => !broth || component.component_type === 'base').map((component) => {
      const perContainerOz = Number(component.portion_oz) > 0 ? Number(component.portion_oz) : null
      const totalOz = perContainerOz == null ? null : perContainerOz * count
      return { id: component.id, name: component.recipe_name || component.name, recipeId: component.recipe_id, count, perContainerOz, totalOz,
        totalLiters: totalOz == null ? null : totalOz / FLUID_OZ_PER_LITER,
        taskKey: broth ? (component.recipe_id ? `chef-broth::${component.recipe_id}` : null) : `side-complete::${dish.dish_id}` }
    })
    return { dishId: dish.dish_id, name: dish.name, kind: broth ? 'broth' : 'shots', count, items }
  }).filter((side) => side.count > 0 || side.items.length > 0)
}

export function taskStatus(inputs, task) {
  const explicit = groupedInput(inputs, 'taskStatus', task)
  if (explicit) return explicit
  return groupedInput(inputs, 'cut', task) === '1' ? 'complete' : 'not_started'
}
export function taskShortage(task) {
  return task.kind === 'weighted' && task.onHand != null ? Math.max(0, task.need - task.onHand) : 0
}
export function taskReady(inputs, task) {
  const target = groupedInput(inputs, 'taskTarget', task)
  if (target != null && Number.isFinite(Number(target)) && task.portions > Number(target)) return false
  return taskStatus(inputs, task) === 'complete' && (task.kind !== 'weighted' || (task.onHand != null && taskShortage(task) < 0.05))
}
// Cooking and the prep team's weigh-in are separate persisted checkoffs.
export function chefTaskStatus(inputs, task) {
  const chefStatus = groupedInput(inputs, 'chefStatus', task)
  if (task.kind === 'weighted') return chefStatus || 'not_started'
  // Sauces and shots have one shared cooking status. Admin corrections must
  // override the older Chef-only field; keep it as a fallback for saved work.
  return groupedInput(inputs, 'taskStatus', task) || chefStatus || taskStatus(inputs, task)
}
export const taskCount = (task) => Number(task?.plates ?? task?.portions) || 0
export function chefTaskNeedsReview(inputs, task) {
  const sections = task.kind === 'weighted' ? ['chefTarget', 'taskTarget'] : ['taskTarget', 'chefTarget']
  const target = groupedInput(inputs, sections[0], task) ?? groupedInput(inputs, sections[1], task)
  return target != null && Number.isFinite(Number(target)) && taskCount(task) > Number(target)
}
export function chefTaskReady(inputs, task) {
  if (chefTaskNeedsReview(inputs, task)) return false
  return chefTaskStatus(inputs, task) === 'complete' && (task.kind !== 'weighted' || taskReady(inputs, task))
}

const WORK_CATEGORIES = [
  ['protein', 'Proteins'], ['carb', 'Carbs'], ['sauce', 'Sauces'], ['broth', 'Broths'],
  ['vegetable', 'Vegetables'], ['base', 'Toppings & bases'],
  ['side-base', 'Side bases'], ['shots', 'Wellness shots'], ['seasoning', 'Seasonings'], ['other', 'Other'],
]
export function groupKitchenItems(items, nameFor = (item) => item.name) {
  const groups = new Map(WORK_CATEGORIES.map(([key, label]) => [key, { key, label, items: [] }]))
  for (const item of items) {
    let category = item.kitchenCategory || item.type || item.category || item.kind
    if (category === 'grain') category = 'carb'
    const group = groups.get(category) || groups.get('other')
    group.items.push(item)
  }
  return [...groups.values()].filter((group) => group.items.length).map((group) => ({
    ...group, items: [...group.items].sort((a, b) => nameFor(a).localeCompare(nameFor(b))),
  }))
}

// Menu broth links take precedence over legacy library categories such as
// vegetable/base, without changing the recipe or its purchasing category.
export function groupChefRecipes(recipes, brothRecipeIds = []) {
  const brothIds = new Set(brothRecipeIds.map(String))
  return groupKitchenItems(recipes.map((recipe) => brothIds.has(String(recipe.db_id ?? recipe.id))
    ? { ...recipe, kitchenCategory: 'broth' } : recipe))
}

export function sideToppingReady(inputs, key, count) {
  return inputs.cut?.[key] === '1' && Number(inputs.taskTarget?.[key]) >= count
}

function isChickenMember(member) {
  const component = member?.component || {}
  const names = [component.name, component.recipe_name, component.raw_ingredient_name].filter(Boolean).join(' ')
  return component.component_type === 'protein' && /\bchicken\b/i.test(names)
}

function summarizeChickenUses(members) {
  const details = members.map((member) => {
    const portions = Number(member.plates) || 0
    const parsedPortion = Number(member.component?.portion_oz)
    const portionOz = Number.isFinite(parsedPortion) && parsedPortion > 0 ? parsedPortion : null
    return {
      dishId: member.dishId,
      dishName: member.dishName || 'Unnamed dish',
      portions,
      portionOz,
      cookedLbs: portionOz == null ? null : (portions * portionOz) / 16,
    }
  })
  return {
    portions: details.reduce((sum, detail) => sum + detail.portions, 0),
    cookedLbs: details.reduce((sum, detail) => sum + (detail.cookedLbs || 0), 0),
    complete: details.every((detail) => detail.cookedLbs != null),
    details,
  }
}

export function chickenCutPlan(row) {
  if (row?.type !== 'protein') return null
  const chicken = (row.members || []).filter(isChickenMember)
  const saladMembers = chicken.filter((member) => SALAD_CATEGORIES.has(member.dishCategory))
  if (!saladMembers.length) return null
  return {
    salad: summarizeChickenUses(saladMembers),
    entree: summarizeChickenUses(chicken.filter((member) => !SALAD_CATEGORIES.has(member.dishCategory))),
  }
}

export function buildKitchenTasks(menu, latest, inputs) {
  const meals = (menu?.dishes || []).filter((dish) => !SIDE_CATEGORIES.includes(dish.category))
  const weighted = [...buildIngredientGroups(meals, inputs).values()].map((row) => ({ ...row,
    kind: row.type === 'base' ? 'check' : 'weighted', area: kitchenResponsibility(row.type, row.name), chickenCutPlan: chickenCutPlan(row),
  }))
  const bases = new Map()
  for (const dish of menu?.dishes || []) {
    if (!FRIDAY_SIDE_CATEGORIES.has(dish.category)) continue
    for (const component of (dish.components || []).filter(isSideBase)) {
      const key = taskKeyForBase(component)
      const task = bases.get(key) || { key, name: component.recipe_name || component.name, kind: 'side-base',
        area: 'prep', recipeId: component.recipe_id, portions: 0, dishes: [] }
      task.portions += sideCount(dish, latest)
      task.dishes.push(dish.name)
      bases.set(key, task)
    }
  }
  const broths = new Map()
  for (const dish of menu?.dishes || []) {
    if (!BROTH_CATEGORIES.has(dish.category)) continue
    for (const component of dish.components || []) {
      if (component.component_type !== 'base' || !component.recipe_id) continue
      const key = `chef-broth::${component.recipe_id}`
      const task = broths.get(key) || { key, name: component.recipe_name || component.name,
        kind: 'broth', area: 'chef', recipeId: component.recipe_id, portions: 0, dishes: [] }
      task.portions += sideCount(dish, latest)
      task.dishes.push(dish.name)
      broths.set(key, task)
    }
  }
  const sauces = menuSauces(menu).map((component) => ({ key: `chef-sauce::${component.recipe_id || component.name.toLowerCase()}`,
    name: component.name, kind: 'sauce', area: 'chef', recipeId: component.recipe_id }))
  const shots = (menu?.dishes || []).filter((dish) => SHOT_CATEGORIES.has(dish.category)).map((dish) => ({
    key: `side-complete::${dish.dish_id}`, name: dish.name, kind: 'shots', area: 'chef', portions: sideCount(dish, latest), components: dish.components || [],
  }))
  return [...weighted, ...bases.values(), ...broths.values(), ...sauces, ...shots].sort((a, b) => a.name.localeCompare(b.name))
}

// A saved manual order is stable when counts change. New dishes are appended and
// called out for review; removed dishes no longer appear. Never renumber by sales.
export function productionSequence(dishes, savedIds = []) {
  const meals = sortPlatingDishes(dishes)
  const byId = new Map(meals.map((dish) => [dish.dish_id ?? dish.id, dish]))
  const saved = [...new Set(savedIds)].filter((id) => byId.has(id))
  const added = meals.filter((dish) => !saved.includes(dish.dish_id ?? dish.id))
  return { dishes: [...saved.map((id) => byId.get(id)), ...added], addedIds: added.map((dish) => dish.dish_id ?? dish.id), needsReview: savedIds.length > 0 && (added.length > 0 || saved.length !== savedIds.length) }
}

export function friendlyMeasure(value, unit = '') {
  const number = Number(value)
  if (!Number.isFinite(number)) return `${value ?? '—'} ${unit}`.trim()
  const spoon = /^(?:tsp|tbsp|teaspoons?|tablespoons?|cups?)$/i.test(unit.trim())
  const rounded = spoon ? Math.round(number * 4) / 4 : Math.round(number * 100) / 100
  // Do not round a small nonzero quantity to zero or shift it by >2%.
  const practical = Math.abs(number) > 0 && Math.abs(number) < 0.001 ? Number(number.toPrecision(3)) : rounded !== 0 && Math.abs(rounded - number) <= Math.abs(number) * 0.02 ? rounded : Math.round(number * 1000) / 1000
  return `${practical.toLocaleString(undefined, { maximumFractionDigits: Math.abs(practical) > 0 && Math.abs(practical) < 0.001 ? 12 : 3 })} ${unit}`.trim()
}
